/**
 * Daily motivation line for the hero panel.
 *
 * Source: dummyjson.com/quotes — free, no key, and it returns the CORS
 * header the browser needs. (api.quotable.io is the usual pick and is
 * what this was written against first; it no longer resolves, so it was
 * dropped rather than left as a dead call.)
 *
 * The catch is that the feed is unvetted — 1,454 quotes mixing business
 * advice with devotional verse and lines about sin. This dashboard is
 * read by employees and students, so the response is filtered on the way
 * in rather than trusted: KEEP is an allow-list of work-and-learning
 * vocabulary, which is safer than trying to enumerate everything that
 * should not appear. Anything that fails it is simply not shown.
 *
 * The API is the nice-to-have, not the feature. If it is slow, blocked
 * by a corporate proxy, or down, CURATED carries the panel — the hero
 * must never render an empty gap where a sentence should be.
 *
 * "Daily" is per day: the rotation starts from an index seeded off the
 * date, so everyone opening the dashboard on the same day starts on the
 * same quote, and it moves on tomorrow.
 */

import { useEffect, useRef, useState } from 'react';

const API = 'https://dummyjson.com/quotes?limit=150&skip=0';

/* A quote is shown only if it speaks to work, learning or effort. */
const KEEP = /\b(work|learn|study|teach|practice|effort|goal|habit|discipline|focus|start|begin|improve|progress|grow|growth|succeed|success|achiev|persist|persever|patient|patience|skill|knowledge|idea|create|build|change|opportunit|challenge|team|lead|time|today|future|dream|courage|confiden|mistake|fail|try|action)\w*\b/i;

/* Dropped regardless of the allow-list. */
const DROP = /\b(god|allah|lord|sin|pray|soul|heaven|hell|prophet|holy|kill|die|death|blood|war|hate|drunk|wine|criminal|weapon|axe|enemy|revenge|apolog|stupid|fool|curse|sacrifice)\w*\b/i;

/* Honorifics that mark a devotional attribution. */
const HONORIFIC = /\(R\.?A\.?\)|\(P\.?B\.?U\.?H\.?\)/i;

const SMALL = /^(a|an|and|as|at|but|by|for|if|in|is|it|nor|of|on|or|so|the|to|up|yet|be|do|not|that|this|you|your|we|our|us|are|was|with|from|can|will|when|what|who|how|all|its|his|her|their|them|he|she|they|than|then|too|out|off|over|into|about|more|most|just|only|even|also|no|yes|has|have|had|been|would|could|should|may|might|must|shall|does|did|am)$/i;

/**
 * A large slice of the feed arrives mangled into Title Case ("The Way To
 * Get Started Is To Quit Talking"), which reads badly as a hero line.
 * Rejecting those threw away most of the usable quotes, so they are
 * repaired instead: lowercase the filler words, keep acronyms, and
 * re-capitalise after sentence breaks.
 */
const fixTitleCase = (text) => {
  const parts = text.split(/(\s+)/);
  let capped = 0;
  let words = 0;
  parts.forEach((t) => {
    if (/^[A-Za-z]{3,}$/.test(t)) {
      words += 1;
      if (/^[A-Z]/.test(t)) capped += 1;
    }
  });
  if (!words || capped / words <= 0.6) return text;

  let atSentenceStart = true;
  const out = parts
    .map((t) => {
      if (!/[A-Za-z]/.test(t)) return t;
      if (/^[A-Z]{2,}$/.test(t)) return t; // acronym
      const low = t.toLowerCase();
      if (atSentenceStart) {
        atSentenceStart = /[.!?]$/.test(t);
        return t.charAt(0).toUpperCase() + low.slice(1);
      }
      atSentenceStart = /[.!?]$/.test(t);
      if (/^i$/.test(low) || /^i['’]/.test(low)) return 'I' + low.slice(1);
      return SMALL.test(low.replace(/[^a-z]/g, '')) || low.length <= 4 ? low : low;
    })
    .join('');

  // "Don'T" -> "Don't"
  return out.replace(/(['’])([A-Z])/g, (m, q, c) => q + c.toLowerCase());
};

/* Short, non-denominational, safe for a mixed audience of employees and
   students. Shown until the fetch lands, kept in the pool afterwards, and
   used on its own if the request fails. */
const CURATED = [
  { content: 'Small steps every day still cross the mountain.', author: 'Unknown' },
  { content: 'Learning is the only thing the mind never exhausts.', author: 'Leonardo da Vinci' },
  { content: 'Quality is not an act, it is a habit.', author: 'Aristotle' },
  { content: 'The expert in anything was once a beginner.', author: 'Helen Hayes' },
  { content: 'Well begun is half done.', author: 'Aristotle' },
  { content: 'Progress, not perfection.', author: 'Unknown' },
  { content: 'Discipline is choosing what you want most over what you want now.', author: 'Unknown' },
  { content: 'A goal without a plan is just a wish.', author: 'Antoine de Saint-Exupéry' },
];

const ROTATE_MS = 9000;

/** Same starting quote for everyone on a given day. */
const seedForToday = (length) => {
  const d = new Date();
  const day = Math.floor(
    Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86400000,
  );
  return length ? day % length : 0;
};

const QuoteTicker = () => {
  const [quotes, setQuotes] = useState(CURATED);
  const [index, setIndex] = useState(() => seedForToday(CURATED.length));
  // Drives the enter animation: flipped off and back on around each change
  // so the CSS animation restarts instead of running only once.
  const [shown, setShown] = useState(true);
  const timer = useRef(null);

  useEffect(() => {
    // AbortController so a route change mid-flight does not set state on
    // an unmounted component.
    const ac = new AbortController();
    const t = setTimeout(() => ac.abort(), 6000);

    fetch(API, { signal: ac.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        const vetted = (data && Array.isArray(data.quotes) ? data.quotes : [])
          .map((q) => ({
            content: fixTitleCase(String(q.quote || '').trim()),
            author: String(q.author || '').trim(),
          }))
          // Short enough for one hero line, and on-topic for this audience.
          .filter(
            (q) =>
              q.content.length >= 20 &&
              q.content.length <= 90 &&
              KEEP.test(q.content) &&
              !DROP.test(q.content) &&
              !HONORIFIC.test(q.author),
          );

        // Keep the curated lines in the pool so the tone stays anchored
        // even when the API contributes a lot.
        if (vetted.length) {
          const pool = [...CURATED, ...vetted].filter(
            (q, i, all) => all.findIndex((o) => o.content === q.content) === i,
          );
          setQuotes(pool);
          setIndex(seedForToday(pool.length));
        }
      })
      .catch(() => {
        /* Offline, blocked or slow — CURATED is already on screen. */
      })
      .finally(() => clearTimeout(t));

    return () => {
      clearTimeout(t);
      ac.abort();
    };
  }, []);

  useEffect(() => {
    if (quotes.length < 2) return undefined;

    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    timer.current = setInterval(() => {
      if (reduced) {
        setIndex((i) => (i + 1) % quotes.length);
        return;
      }
      // Play the exit, swap at the end of it, then play the enter.
      setShown(false);
      setTimeout(() => {
        setIndex((i) => (i + 1) % quotes.length);
        setShown(true);
      }, 420);
    }, ROTATE_MS);

    return () => clearInterval(timer.current);
  }, [quotes.length]);

  const quote = quotes[index % quotes.length];
  if (!quote) return null;

  return (
    <figure
      className={`sm-quote${shown ? ' is-in' : ' is-out'}`}
      aria-live="polite"
      aria-atomic="true"
    >
      <span className="sm-quote-mark" aria-hidden="true">&ldquo;</span>
      <blockquote className="sm-quote-text">{quote.content}</blockquote>
      <figcaption className="sm-quote-author">{quote.author}</figcaption>
    </figure>
  );
};

export default QuoteTicker;
